
import React from "react";
import { RefreshCw } from "lucide-react";
import ConversationHeader from "@/components/refund/conversation/ConversationHeader";
import ConversationDetails from "@/components/refund/ConversationDetails";
import ConversationThread from "@/components/refund/ConversationThread";
import MessageFormatSection from "./MessageFormatSection";
import FloatingClarificationButton from "./FloatingClarificationButton";
import { Conversation } from "@/types/conversation";
import { Message, DBMessage, MessageTone } from "@/types/message";
import { useConversationHandlers } from "./ConversationHandlers";
import { debugMessageFormatUpdate } from "@/utils/debug-message-format";
import type { ClarificationType } from "@/components/refund/ClarificationInput";

interface ConversationContentProps {
  conversation: Conversation;
  uiMessages: Message[];
  dbMessages: DBMessage[];
  threadRef: React.RefObject<HTMLDivElement>;
  conversationData: any;
  isClarificationSubmitting: boolean;
  setIsClarificationSubmitting: (submitting: boolean) => void;
  onRegenerateAIResponse: (messageId: string, tone: MessageTone, customInstructions?: string) => Promise<Message | null>;
  onFormatUpdate?: (format: "email" | "chat") => void;
}

const ConversationContent = ({
  conversation,
  uiMessages,
  dbMessages,
  threadRef,
  conversationData,
  isClarificationSubmitting,
  setIsClarificationSubmitting,
  onRegenerateAIResponse,
  onFormatUpdate
}: ConversationContentProps) => {
  const { handleClarificationSubmit, handleGenerateFollowUp } = useConversationHandlers({
    conversation,
    messages: dbMessages,
    handleGenerateResponse: conversationData.handleGenerateResponse,
    setIsClarificationSubmitting
  });

  const findLastRelevantAIMessage = (messages: Message[]): Message | null => {
    if (!messages || messages.length === 0) return null;
    
    const aiMessages = messages
      .filter(msg => msg.type === "ai")
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    return aiMessages.length > 0 ? aiMessages[0] : null;
  };

  const lastAIMessage = findLastRelevantAIMessage(uiMessages);
  const hasAIMessages = uiMessages.some(msg => msg.type === "ai");
  const userEmail = conversation.user_email || "user@example.com";
  const sellerEmail = conversation.merchant_email || 
    `support@${conversation.merchant?.toLowerCase().replace(/\s+/g, '')}.com`;

  // Debug function - available in console
  React.useEffect(() => {
    if (typeof window !== 'undefined' && conversation?.id) {
      (window as any).debugMessageFormat = () => debugMessageFormatUpdate(conversation.id);
      console.log("Debug function available: window.debugMessageFormat()");
    }
  }, [conversation?.id]);

  if (conversationData.loading) {
    return (
      <div className="bg-white min-h-screen">
        <div className="container py-8 flex flex-col items-center justify-center min-h-[400px]">
          <RefreshCw className="animate-spin h-8 w-8 text-primary mb-4" />
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="container py-8">
        <ConversationHeader 
          conversationId={conversation.id} 
          companyName={conversation.merchant}
        />
        
        {/* Only show message format section for regular refund requests */}
        {conversation.request_type !== "flight_compensation" && (
          <MessageFormatSection 
            conversation={conversation} 
            onFormatUpdate={onFormatUpdate}
            onRegenerateLatestAI={lastAIMessage ? onRegenerateAIResponse : undefined}
            latestAIMessageId={lastAIMessage?.id}
            isRegenerating={conversationData.isRegenerating}
          />
        )}
        
        <ConversationDetails 
          conversation={conversation}
          messages={uiMessages}
        />
        
        <div className="mb-6" ref={threadRef} id="conversation-thread">
          <h2 className="font-semibold mb-4">Conversation History</h2>
          <ConversationThread 
            messages={uiMessages}
            companyName={conversation.merchant || "Merchant"}
            orderNumber={conversation.order_number || conversation.id.substring(0, 8)}
            onPasteSellerResponse={conversationData.handlePasteSellerResponse}
            onRegenerateAIResponse={onRegenerateAIResponse}
            isAIGenerating={conversationData.isGenerating}
            isRegenerating={conversationData.isRegenerating}
            regeneratingMessageId={conversationData.regeneratingMessageId}
            isPasting={conversationData.isPasting}
            isProcessingAI={conversationData.isProcessingAI}
            userEmail={userEmail}
            sellerEmail={sellerEmail}
            lastAIMessage={lastAIMessage}
            conversation={conversation}
            dbMessages={dbMessages}
            onClarificationSubmit={handleClarificationSubmit}
            onGenerateFollowUp={handleGenerateFollowUp}
            isClarificationSubmitting={isClarificationSubmitting}
          />
        </div>

        {/* Floating Clarification Button */}
        <FloatingClarificationButton
          onClarificationSubmit={handleClarificationSubmit}
          isSubmitting={isClarificationSubmitting}
          hasAIMessages={hasAIMessages}
        />
      </div>
    </div>
  );
};

export default ConversationContent;
